import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';
import Icon from '../../../components/base/Icon';

const QUOTE_FORM_URL = 'https://readdy.ai/api/form/d6p55vp30mftb86r3uq0';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation', label: 'Fence Installation' },
  { value: 'Fence Repair', label: 'Fence Repair' },
  { value: 'Gate Installation', label: 'Gate Installation' },
  { value: 'Fence Replacement', label: 'Fence Replacement' },
  { value: 'Other Fence Work', label: 'Other Fence Work' },
];

interface QuoteFormState {
  full_name: string;
  phone: string;
  email: string;
  service_needed: string;
  project_details: string;
}

const emptyForm = (): QuoteFormState => ({
  full_name: '',
  phone: '',
  email: '',
  service_needed: '',
  project_details: '',
});

function CostGuideQuoteForm({ formId }: { formId: string }) {
  const [values, setValues] = useState<QuoteFormState>(emptyForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof QuoteFormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(QUOTE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm';

  return (
    <form onSubmit={handleSubmit} data-readdy-form id={formId} noValidate>
      {status === 'success' && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-4 h-4" /></span>
          <span>Thanks — we received your quote request and will contact you within 24 hours.</span>
        </div>
      )}
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-exclamation" className="w-4 h-4" /></span>
          <span>{errorMsg}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor={`${formId}-full_name`} className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input type="text" id={`${formId}-full_name`} name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
          <input type="tel" id={`${formId}-phone`} name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-email`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input type="email" id={`${formId}-email`} name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
      </div>
      <div className="mb-4">
        <label htmlFor={`${formId}-service_needed`} className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
        <select id={`${formId}-service_needed`} name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mb-6">
        <label htmlFor={`${formId}-project_details`} className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
        <textarea
          id={`${formId}-project_details`}
          name="project_details"
          required
          rows={3}
          maxLength={500}
          value={values.project_details}
          onChange={handleChange}
          className={`${inputClass} resize-none`}
          placeholder="Describe your fence project — material preference, linear footage, property type..."
        />
        <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
      </div>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
      >
        {status === 'submitting' ? 'Sending...' : 'Request Free Fence Quote'}
      </button>
    </form>
  );
}

const materialRows = [
  {
    material: 'Wood (Cedar)',
    pros: 'Natural look, customizable, paintable/stainable, easy to repair',
    cons: 'Needs periodic sealing/staining, can rot if untreated',
    maintenance: 'Stain or seal every 2–3 years; inspect posts annually',
    typicalUse: 'Privacy fences, picket fences, decorative backyard borders',
    lifespan: '15–30 yrs',
  },
  {
    material: 'Wood (Pine)',
    pros: 'Lower upfront cost than cedar, widely available',
    cons: 'Less rot-resistant than cedar, shorter lifespan without treatment',
    maintenance: 'Seal every 1–2 years; more prone to warping in Idaho heat',
    typicalUse: 'Budget-conscious installs, temporary or rental properties',
    lifespan: '10–20 yrs',
  },
  {
    material: 'Vinyl',
    pros: 'Very low maintenance, won\'t rot or rust, holds color well',
    cons: 'Higher upfront cost, can crack in hard freezes, fewer styles',
    maintenance: 'Rinse with a hose; no staining or sealing needed',
    typicalUse: 'HOA communities, pool enclosures, homeowners wanting zero upkeep',
    lifespan: '20–40 yrs',
  },
  {
    material: 'Chain Link',
    pros: 'Most affordable, very durable, low maintenance, good for large areas',
    cons: 'No privacy, less curb appeal, can rust without coating',
    maintenance: 'Inspect for rust spots; touch up coating as needed',
    typicalUse: 'Dog runs, back yards, large perimeters, utility areas',
    lifespan: '20–30 yrs',
  },
];

const costRanges = [
  {
    type: 'Wood Privacy Fence (6 ft)',
    unit: 'per linear ft installed',
    low: '$18–$25',
    high: '$35–$50+',
    dependsOn: 'Cedar vs. pine, board style, site prep, old fence removal',
    scenario: '150 linear ft cedar privacy fence: roughly $3,500–$6,500 installed',
  },
  {
    type: 'Wood Picket Fence (3–4 ft)',
    unit: 'per linear ft installed',
    low: '$12–$18',
    high: '$25–$35',
    dependsOn: 'Picket spacing, decorative caps, post spacing',
    scenario: '100 linear ft front yard picket fence: roughly $1,500–$3,200 installed',
  },
  {
    type: 'Vinyl Privacy Fence (6 ft)',
    unit: 'per linear ft installed',
    low: '$25–$35',
    high: '$45–$65+',
    dependsOn: 'Panel style, brand quality, post depth required',
    scenario: '150 linear ft vinyl privacy fence: roughly $4,500–$9,000 installed',
  },
  {
    type: 'Chain Link (4–6 ft)',
    unit: 'per linear ft installed',
    low: '$10–$15',
    high: '$20–$30',
    dependsOn: 'Height, wire gauge, galvanized vs. vinyl-coated',
    scenario: '200 linear ft chain link: roughly $2,500–$5,500 installed',
  },
  {
    type: 'Walk-Through Gate (wood/vinyl)',
    unit: 'per gate installed',
    low: '$200–$400',
    high: '$600–$1,000+',
    dependsOn: 'Width, material, hardware quality',
    scenario: 'Single 4-ft walk gate with latch: roughly $300–$600',
  },
  {
    type: 'Double Drive Gate',
    unit: 'per gate installed',
    low: '$600–$1,200',
    high: '$1,800–$3,500+',
    dependsOn: 'Width, material, automatic opener add-on',
    scenario: '12-ft double wood drive gate: roughly $900–$2,200',
  },
  {
    type: 'Post Replacement',
    unit: 'per post',
    low: '$150–$250',
    high: '$350–$500+',
    dependsOn: 'Concrete removal, post depth, material type',
    scenario: 'Replacing 3 rotted posts: roughly $600–$1,200',
  },
  {
    type: 'Fence Repair (boards/panels)',
    unit: 'per repair visit',
    low: '$100–$200',
    high: '$300–$600+',
    dependsOn: 'Number of boards, material match, travel distance',
    scenario: 'Replacing 8 storm-damaged boards: roughly $200–$450',
  },
];

const costDrivers = [
  {
    icon: 'ruler',
    title: 'Linear Footage',
    desc: 'The single biggest cost factor. More fence = more cost, but price per foot often drops slightly on larger jobs due to material efficiency.',
  },
  {
    icon: 'layer-group',
    title: 'Material Choice',
    desc: 'Chain link is cheapest upfront; vinyl costs more but saves on maintenance. Cedar sits in the middle and performs well in Boise\'s climate.',
  },
  {
    icon: 'arrows-up-down',
    title: 'Fence Height',
    desc: 'Taller fences require more material per linear foot and deeper post holes. Going from 4 ft to 6 ft can add 30–50% to material costs.',
  },
  {
    icon: 'mountain',
    title: 'Site Conditions',
    desc: 'Rocky or clay-heavy soil (common in parts of Boise) increases labor time. Slopes, tree roots, and uneven terrain all add cost.',
  },
  {
    icon: 'trash',
    title: 'Old Fence Removal',
    desc: 'Demo and haul-away typically adds $3–$8 per linear foot. Removing it yourself before we arrive can save a meaningful amount.',
  },
  {
    icon: 'door-open',
    title: 'Gates',
    desc: 'Each gate adds cost — especially double drive gates or automatic openers. Budget $200–$3,500+ per gate depending on size and type.',
  },
  {
    icon: 'file-contract',
    title: 'Permits & HOA',
    desc: 'City permit fees typically run $50–$200 for residential fences. HOA approval is free but may require specific materials that cost more.',
  },
  {
    icon: 'calendar',
    title: 'Season & Timing',
    desc: 'Spring and summer are peak season — demand is high and scheduling is tighter. Fall and winter installs may have more flexibility.',
  },
];

const scenarios = [
  {
    title: 'Scenario A: Standard Backyard Privacy Fence',
    desc: 'Boise homeowner, 150 linear feet, 6-ft cedar privacy fence, flat lot, no old fence to remove, one walk-through gate.',
    breakdown: [
      { item: 'Cedar privacy fence (150 lf × ~$28/lf)', cost: '$4,200' },
      { item: 'Walk-through gate (1)', cost: '$450' },
      { item: 'Concrete & hardware', cost: 'Included' },
      { item: 'Permit (if required)', cost: '$75–$150' },
    ],
    total: '$4,650–$5,000 estimated',
    note: 'Flat lot, no demo, standard cedar boards. Actual quote may vary.',
  },
  {
    title: 'Scenario B: Full Yard Vinyl Fence with Drive Gate',
    desc: 'Meridian homeowner, 200 linear feet, 6-ft white vinyl, HOA-required material, one double drive gate, old wood fence to remove.',
    breakdown: [
      { item: 'Vinyl fence (200 lf × ~$40/lf)', cost: '$8,000' },
      { item: 'Double drive gate (12 ft)', cost: '$1,400' },
      { item: 'Old fence demo & haul-away (200 lf × $5)', cost: '$1,000' },
      { item: 'Permit', cost: '$100–$200' },
    ],
    total: '$10,500–$11,000 estimated',
    note: 'HOA-required vinyl, demo included, drive gate. Actual quote may vary.',
  },
  {
    title: 'Scenario C: Budget Chain Link for Large Property',
    desc: 'Nampa property owner, 300 linear feet, 4-ft galvanized chain link, large flat lot, no old fence, two walk-through gates.',
    breakdown: [
      { item: 'Chain link (300 lf × ~$14/lf)', cost: '$4,200' },
      { item: 'Walk-through gates (2 × $300)', cost: '$600' },
      { item: 'Concrete & hardware', cost: 'Included' },
      { item: 'Permit (if required)', cost: '$75' },
    ],
    total: '$4,875–$5,200 estimated',
    note: 'Galvanized chain link, large perimeter, budget-focused. Actual quote may vary.',
  },
];

const estimateChecklist = [
  'Measure your property perimeter (or the section you want fenced) — linear footage is the #1 cost driver',
  'Know your HOA rules before calling — material, height, and color restrictions affect your options',
  'Decide if you want to remove the old fence yourself or have us include demo in the quote',
  'Note any slopes, tree roots, rocky areas, or drainage issues on your property',
  'Count how many gates you need and what type (walk-through vs. drive gate)',
  'Check if a permit is required for your city/zone — we can help you figure this out',
  'Have a rough idea of your budget range so we can recommend the right material',
  'Be ready to share your address so we can assess site conditions before the visit',
];

const faqs = [
  {
    q: 'What\'s the average cost to install a fence in Boise, Idaho?',
    shortA: 'Most residential fences run $3,000–$10,000 installed.',
    fullA: 'For a typical Boise residential fence project — 100–200 linear feet of 6-ft privacy fencing — expect to pay roughly $3,000–$10,000 installed depending on material. Wood (cedar) tends to land in the $3,500–$7,000 range for that footage. Vinyl runs higher, typically $5,000–$12,000. Chain link is the most affordable at $2,000–$5,000. These are real Treasure Valley ranges, not national averages.',
  },
  {
    q: 'How much does a fence cost per linear foot in Boise?',
    shortA: 'Roughly $10–$65 per linear foot installed, depending on material.',
    fullA: 'Chain link runs $10–$30/lf installed. Wood privacy fencing runs $18–$50+/lf. Vinyl runs $25–$65+/lf. These ranges include labor, materials, posts, and concrete. Gates are priced separately. The per-foot price often drops slightly on larger jobs due to material efficiency.',
  },
  {
    q: 'Is cedar or vinyl cheaper for a Boise fence?',
    shortA: 'Cedar is cheaper upfront; vinyl costs less over time.',
    fullA: 'Cedar typically costs $18–$50/lf installed vs. $25–$65+/lf for vinyl. So cedar wins on upfront cost. But vinyl requires almost no maintenance — no staining, sealing, or painting. Over a 20-year period, the total cost of ownership can be similar or even favor vinyl. If you plan to stay in your home long-term, vinyl is worth the premium. If you\'re selling in 5–10 years, cedar is usually the smarter financial choice.',
  },
  {
    q: 'Does fence installation cost more in spring and summer?',
    shortA: 'Not necessarily more expensive, but harder to schedule.',
    fullA: 'Fence installation pricing doesn\'t typically change by season — but availability does. Spring and summer (April–July) are peak season in Boise. Contractors book out 2–4 weeks during this period. Fall and winter have more availability and you may get faster scheduling. Pricing is generally consistent year-round, though some contractors may offer off-season discounts.',
  },
  {
    q: 'What\'s included in a fence installation quote from Fix Right Services?',
    shortA: 'Labor, materials, posts, concrete, and cleanup.',
    fullA: 'Our written estimates include all labor, materials (posts, boards or panels, hardware), concrete for post setting, and site cleanup. We\'ll clearly note any exclusions — for example, if old fence removal, significant grading, or tree root clearing is needed, that\'s a separate line item. No hidden fees. You\'ll know exactly what you\'re paying for before any work starts.',
  },
  {
    q: 'How much does it cost to add a gate to an existing fence in Boise?',
    shortA: 'Walk-through gates run $200–$1,000; drive gates $600–$3,500+.',
    fullA: 'A standard walk-through gate (3–4 ft wide, wood or vinyl) typically costs $200–$600 installed. Wider or heavier gates run $600–$1,000+. Double drive gates (10–14 ft wide) run $600–$3,500+ depending on material and whether you add an automatic opener. Hardware quality, post reinforcement, and material all affect the final price.',
  },
  {
    q: 'Do I need a permit for a fence in Boise, and does it add to the cost?',
    shortA: 'Depends on height and location — permit fees are typically $50–$200.',
    fullA: 'In Boise, rear and side yard fences under 6 feet typically don\'t require a permit. Front yard fences and anything over 6 feet usually do. Permit fees for residential fences in Boise and Ada County typically run $50–$200. We handle the permit application process as part of your project — the fee is passed through at cost with no markup.',
  },
  {
    q: 'Can I save money by removing my old fence before you arrive?',
    shortA: 'Yes — demo and haul-away typically adds $3–$8 per linear foot.',
    fullA: 'If you remove and dispose of your old fence before our crew arrives, you can save $3–$8 per linear foot on demo and haul-away costs. On a 150-foot fence, that\'s $450–$1,200 in potential savings. Just make sure all old posts are fully removed (not just cut off at ground level) — buried post stubs can complicate new post installation.',
  },
];

export default function FenceInstallationCostGuideBoise() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useSEO({
    title: 'Fence Installation Cost Guide Boise Idaho 2025 | Fix Right Services',
    description: 'Complete fence cost guide for Boise & Treasure Valley. Real per-foot price ranges for wood, vinyl & chain link. Cost drivers, example scenarios, and how to get an accurate estimate. Call (208) 204-6331.',
    keywords: 'fence installation cost Boise Idaho, fence cost per foot Boise, wood fence cost Boise, vinyl fence price Treasure Valley, chain link fence cost Idaho, fence estimate Boise',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/blog/fence-installation-cost-guide-boise-idaho`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': 'Fence Installation Cost Guide: Boise & Treasure Valley, Idaho (2025)',
      'description': 'Complete cost guide for fence installation in Boise, Idaho. Real price ranges by material, cost drivers, example scenarios, and how to get an accurate estimate.',
      'author': {
        '@type': 'Organization',
        'name': 'Fix Right Services',
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Fix Right Services',
        'url': import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com',
      },
      'datePublished': '2025-01-20',
      'dateModified': new Date().toISOString().split('T')[0],
    },
  });

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-8">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </Link>
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700">
                <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
                <Link to="/services" className="hover:text-[#B45309] transition-colors cursor-pointer">Services</Link>
                <Link to="/services/fence" className="hover:text-[#B45309] transition-colors cursor-pointer">Fence</Link>
                <Link to="/blog" className="text-[#B45309] font-semibold cursor-pointer">Blog</Link>
              </div>
            </div>
            <a
              href="tel:+12082046331"
              className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Fix Right Services"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              <span className="hidden sm:inline">(208) 204-6331</span>
              <span className="sm:hidden">Call Now</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Article hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f093b9570340f1e36f4d118cbfc39592.jpg"
            alt="Fence installation cost guide Boise Idaho"
            width="1920"
            height="700"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/65"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto w-full">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/70 flex-wrap">
              <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li><Link to="/blog" className="hover:text-white transition-colors cursor-pointer">Blog</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li className="text-white font-medium">Fence Cost Guide</li>
            </ol>
          </nav>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="inline-flex items-center gap-1.5 bg-[#B45309]/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
              <span className="w-4 h-4 flex items-center justify-center"><Icon name="border-all" className="w-3 h-3" /></span>
              Fence Installation
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-full">
              <span className="w-4 h-4 flex items-center justify-center"><Icon name="calendar" className="w-3 h-3" /></span>
              Updated January 2025
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-full">
              <span className="w-4 h-4 flex items-center justify-center"><Icon name="clock" className="w-3 h-3" /></span>
              8 min read
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            Fence Installation Cost Guide: Boise &amp; Treasure Valley, Idaho (2025)
          </h1>
          <p className="text-lg text-white/90 max-w-3xl leading-relaxed">
            Real per-foot price ranges, material comparisons, cost drivers, and example scenarios — based on what we actually charge in the Boise market.
          </p>
        </div>
      </section>

      {/* Quick Answer */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-amber-50 border-b border-amber-100" aria-label="Quick answer summary">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#B45309] rounded-xl flex-shrink-0 mt-1">
              <Icon name="bolt" className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold text-[#B45309] uppercase tracking-widest mb-2">Quick Answer</p>
              <p className="text-gray-800 text-lg leading-relaxed font-medium">
                Fence installation in Boise typically costs <strong>$10–$65 per linear foot installed</strong>, depending on material. Chain link is the most affordable ($10–$30/lf), wood privacy fencing runs $18–$50+/lf, and vinyl runs $25–$65+/lf. For a typical 150-foot backyard fence, expect to budget <strong>$3,000–$10,000 all-in</strong>. Gates, demo, permits, and site conditions all affect the final number.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="py-16 px-4 sm:px-6 lg:px-8" aria-label="Cost guide content">
        <div className="max-w-4xl mx-auto">

          {/* Cost Drivers */}
          <section className="mb-16" aria-labelledby="cost-drivers-heading">
            <h2 id="cost-drivers-heading" className="text-3xl font-bold text-gray-900 mb-3">What Drives Fence Costs in Boise</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Before looking at price ranges, it helps to understand what actually moves the needle on your quote. These are the eight factors that most affect what you&apos;ll pay for fence installation in the Treasure Valley.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {costDrivers.map((driver) => (
                <div key={driver.title} className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#B45309]/10 rounded-lg flex-shrink-0">
                    <Icon name={driver.icon} className="w-5 h-5 text-[#B45309]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm mb-1">{driver.title}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{driver.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Cost Per Foot Table */}
          <section className="mb-16" aria-labelledby="cost-ranges-heading">
            <h2 id="cost-ranges-heading" className="text-3xl font-bold text-gray-900 mb-3">Cost-Per-Foot Ranges by Material</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              These ranges reflect real Boise-area pricing as of 2025 — not national averages. All prices are <strong>installed</strong> (labor + materials + concrete). Gates are priced separately.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="text-left px-5 py-4 font-semibold">Fence Type / Service</th>
                    <th className="text-left px-5 py-4 font-semibold">Low End</th>
                    <th className="text-left px-5 py-4 font-semibold">High End</th>
                    <th className="text-left px-5 py-4 font-semibold hidden lg:table-cell">Depends On</th>
                  </tr>
                </thead>
                <tbody>
                  {costRanges.map((row, idx) => (
                    <tr key={row.type} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-4 font-medium text-gray-900">
                        {row.type}
                        <span className="block text-xs text-gray-500 font-normal">{row.unit}</span>
                      </td>
                      <td className="px-5 py-4 text-green-700 font-semibold whitespace-nowrap">{row.low}</td>
                      <td className="px-5 py-4 text-[#B45309] font-semibold whitespace-nowrap">{row.high}</td>
                      <td className="px-5 py-4 text-gray-600 text-xs hidden lg:table-cell">{row.dependsOn}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 italic">
              * Ranges reflect Boise/Treasure Valley market conditions as of 2025. Material costs fluctuate. Your written estimate will reflect current pricing.
            </p>
          </section>

          {/* Material Comparison Table */}
          <section className="mb-16" aria-labelledby="material-table-heading">
            <h2 id="material-table-heading" className="text-3xl font-bold text-gray-900 mb-3">Material Comparison: Pros, Cons &amp; Maintenance</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Every material has trade-offs. Here&apos;s a side-by-side breakdown to help you choose the right one for your budget, HOA rules, and long-term goals.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="text-left px-5 py-4 font-semibold min-w-[120px]">Material</th>
                    <th className="text-left px-5 py-4 font-semibold min-w-[180px]">Pros</th>
                    <th className="text-left px-5 py-4 font-semibold min-w-[180px]">Cons</th>
                    <th className="text-left px-5 py-4 font-semibold min-w-[180px]">Maintenance</th>
                    <th className="text-left px-5 py-4 font-semibold min-w-[160px]">Typical Use Case</th>
                    <th className="text-left px-5 py-4 font-semibold min-w-[100px]">Lifespan</th>
                  </tr>
                </thead>
                <tbody>
                  {materialRows.map((row, idx) => (
                    <tr key={row.material} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-4 font-semibold text-gray-900 align-top">{row.material}</td>
                      <td className="px-5 py-4 text-gray-700 text-xs leading-relaxed align-top">{row.pros}</td>
                      <td className="px-5 py-4 text-gray-700 text-xs leading-relaxed align-top">{row.cons}</td>
                      <td className="px-5 py-4 text-gray-700 text-xs leading-relaxed align-top">{row.maintenance}</td>
                      <td className="px-5 py-4 text-gray-700 text-xs leading-relaxed align-top">{row.typicalUse}</td>
                      <td className="px-5 py-4 text-gray-700 text-xs font-medium align-top whitespace-nowrap">{row.lifespan}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Example Scenarios */}
          <section className="mb-16" aria-labelledby="scenarios-heading">
            <h2 id="scenarios-heading" className="text-3xl font-bold text-gray-900 mb-3">Example Scenarios: What Real Projects Cost</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              These are illustrative estimates based on typical Boise-area projects. Your actual quote will depend on your specific site, material choices, and current pricing. These are meant to give you a realistic ballpark — not a binding number.
            </p>
            <div className="space-y-6">
              {scenarios.map((scenario, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="bg-gray-900 px-6 py-4">
                    <h3 className="text-white font-bold text-base">{scenario.title}</h3>
                    <p className="text-gray-400 text-sm mt-1">{scenario.desc}</p>
                  </div>
                  <div className="p-6">
                    <div className="space-y-2 mb-4">
                      {scenario.breakdown.map((line) => (
                        <div key={line.item} className="flex justify-between items-center text-sm">
                          <span className="text-gray-700">{line.item}</span>
                          <span className="font-semibold text-gray-900 whitespace-nowrap ml-4">{line.cost}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                      <span className="font-bold text-gray-900">Estimated Total</span>
                      <span className="font-bold text-[#B45309] text-lg">{scenario.total}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 italic">{scenario.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Get an Accurate Estimate */}
          <section className="mb-16" aria-labelledby="estimate-checklist-heading">
            <h2 id="estimate-checklist-heading" className="text-3xl font-bold text-gray-900 mb-3">How to Get an Accurate Estimate</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              The only way to know your real cost is a free on-site estimate. But you can make that visit faster and more productive — and get a more accurate quote — by coming prepared. Here&apos;s the checklist we recommend for every Boise homeowner before calling a fence contractor.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-5 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center"><Icon name="clipboard-list" className="w-5 h-5 text-[#B45309]" /></span>
                Pre-Estimate Checklist
              </h3>
              <ul className="space-y-3">
                {estimateChecklist.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                    <span className="w-6 h-6 flex items-center justify-center bg-[#B45309] rounded-full flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs font-bold">{idx + 1}</span>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
                  <Icon name="lightbulb" className="w-4 h-4 text-green-600" />
                </span>
                <div>
                  <p className="font-semibold text-gray-900 text-sm mb-1">Pro tip: Get at least two written estimates</p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Written estimates let you compare apples to apples — make sure each one includes the same scope (linear footage, material, gate count, demo if needed). A verbal quote or a number texted to you isn&apos;t a real estimate. If a contractor won&apos;t put it in writing, that&apos;s a red flag.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </article>

      {/* CTA Banner */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-[#B45309]" aria-label="Get a quote CTA">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-xl mb-1">Ready to get your real number?</p>
            <p className="text-white/85 text-sm">Free on-site estimates. Response within 24 hours. No pressure, no obligation.</p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-[#B45309] px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a
              href="#cost-guide-quote"
              className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white/10 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Request Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Fence cost FAQs">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Fence Cost FAQs</h2>
            <p className="text-gray-600">The most common pricing questions we get from Boise homeowners — answered straight.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  aria-expanded={openFaq === idx}
                >
                  <div className="pr-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">
                      <a id={`faq-cost-${idx}`}>{faq.q}</a>
                    </h4>
                    <p className="text-[#B45309] text-sm font-medium">{faq.shortA}</p>
                  </div>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-[#B45309]">
                    <Icon name={openFaq === idx ? 'chevron-up' : 'chevron-down'} className="w-4 h-4" />
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.fullA}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                'mainEntity': faqs.map((faq) => ({
                  '@type': 'Question',
                  'name': faq.q,
                  'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': faq.shortA + ' ' + faq.fullA,
                  },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="cost-guide-quote" aria-label="Request a fence quote">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Get Your Free Fence Estimate</h2>
            <p className="text-gray-600">
              Tell us about your project and we&apos;ll get back to you within 24 hours with a free, no-obligation written estimate.
            </p>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4 text-[#B45309]" /></span>
              Prefer to call?&nbsp;
              <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>
            </div>
          </div>
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8">
            <CostGuideQuoteForm formId="cost-guide-fence-quote" />
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Related fence resources">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">More Fence Resources</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Fence Installation & Repair — Service Page', slug: '/services/fence', desc: 'Full overview of materials, process, and what to expect from Fix Right Services.' },
              { title: 'Wood vs. Vinyl Fence: Treasure Valley Guide', slug: '/blog/wood-vs-vinyl-fence-treasure-valley', desc: 'Side-by-side comparison to help you choose the right material for your property.' },
              { title: 'Fence Installation Timeline in Boise', slug: '/blog/fence-installation-timeline-boise', desc: 'What to expect from first call to finished fence — week by week.' },
              { title: 'Best Fence Styles for Boise Neighborhoods', slug: '/blog/best-fence-styles-boise-neighborhoods', desc: 'Which fence styles work best in North End, Meridian, Eagle, and beyond.' },
              { title: 'Signs Your Boise Fence Needs Replacement', slug: '/blog/signs-boise-fence-needs-replacement', desc: 'How to tell if repair is enough or if it\'s time for a full replacement.' },
              { title: 'Fence Installation in Boise — Location Page', slug: '/locations/fence-installation-boise-idaho', desc: 'Boise-specific info: neighborhoods, HOA rules, permits, and local trust signals.' },
            ].map((post) => (
              <Link
                key={post.slug}
                to={post.slug}
                className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md hover:border-[#B45309]/30 transition-all cursor-pointer group"
              >
                <h3 className="font-semibold text-gray-900 text-sm mb-2 group-hover:text-[#B45309] transition-colors">{post.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed mb-3">{post.desc}</p>
                <span className="text-[#B45309] text-xs font-semibold flex items-center gap-1">
                  Read more
                  <span className="w-4 h-4 flex items-center justify-center"><Icon name="arrow-right" className="w-3 h-3" /></span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp" alt="Fix Right Services" width="180" height="48" className="h-12 w-auto mb-4" loading="lazy" />
              <p className="text-gray-400 text-sm">Boise&apos;s trusted fence installation and repair experts serving the entire Treasure Valley.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Fence Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services/fence" className="hover:text-white transition-colors cursor-pointer">Fence Installation &amp; Repair</Link></li>
                <li><Link to="/blog/fence-installation-cost-guide-boise-idaho" className="hover:text-white transition-colors cursor-pointer">Fence Cost Guide</Link></li>
                <li><Link to="/blog/fence-installation-timeline-boise" className="hover:text-white transition-colors cursor-pointer">Timeline Guide</Link></li>
                <li><Link to="/blog/wood-vs-vinyl-fence-treasure-valley" className="hover:text-white transition-colors cursor-pointer">Wood vs. Vinyl Guide</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">All Services</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog &amp; Tips</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <span>Boise, Meridian, Nampa &amp; Treasure Valley</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
